import { refreshToken } from '@hermes/api-sdk'
import { useAuthStore } from '@lib/stores/auth'
import { isAxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import { useSignOut } from './useSignOut'
import { useUserToken } from './useUserToken'

const REFRESH_INTERVAL = 60000 // 1 minute
const REFRESH_MARGIN = 120000 // 2 minutes

const useTokenValidation = () => {
  const accessJWT = useAuthStore((state) => state.accessJWT)
  const refreshJWT = useAuthStore((state) => state.refreshJWT)
  const setTokenExpiresAt = useAuthStore((state) => state.setTokenExpiresAt)
  const { signOut } = useSignOut()
  const { setToken } = useUserToken()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const stopRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const refresh = async () => {
    const { tokenExpiresAt } = useAuthStore.getState() // needs to be reactive each time
    if (tokenExpiresAt && tokenExpiresAt > Date.now() + REFRESH_MARGIN) {
      return true
    }
    try {
      const result = await refreshToken({ accessJWT, refreshJWT })
      setToken(result.accessToken)
      setTokenExpiresAt(Date.now() + result.expiresIn * 1000)
      return true
    } catch (error) {
      // TODO: better error handling of failed refresh (dont signOut and stop refresh on first fail)
      signOut()
      stopRefresh()
      if (isAxiosError(error)) {
        const hermesError = error?.response?.data
        const message = hermesError?.message || 'check your internet connection'
        console.error(message)
      }
      return false
    }
  }

  const startRefresh = () => {
    if (intervalRef.current) {
      return
    }

    intervalRef.current = setInterval(() => {
      refresh()
    }, REFRESH_INTERVAL)
  }

  useEffect(
    () => () => {
      stopRefresh()
    },
    []
  )

  return {
    refresh,
    startRefresh,
    stopRefresh
  }
}

export { useTokenValidation }
